import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const clients = [
  {
    id: 1,
    name: "Crystal",
    logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_legal-advisory-pro/artifacts/khgrechy_CRYSTAL-logo-png.webp",
    alt: "Crystal - Forward, Together"
  },
  {
    id: 2,
    name: "Mudraa",
    logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_legal-advisory-pro/artifacts/t2n1rwzw_MUDRAA-LOGO.jpeg",
    alt: "Mudraa"
  },
  {
    id: 3,
    name: "Taj Citrus Park",
    logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_legal-advisory-pro/artifacts/34d5a5c0544b4ace_rerahub%20client%20logo.jpeg",
    alt: "Taj Citrus Park - A refreshing change"
  }
];

const ClientCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % clients.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + clients.length) % clients.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % clients.length);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  return (
    <div className="relative max-w-md mx-auto" data-testid="client-carousel">
      {/* Carousel Container */}
      <div className="relative flex items-center justify-center">
        {/* Arrow Left */}
        <button
          onClick={goToPrevious}
          className="absolute left-0 z-10 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 shadow-sm hover:border-[#C9A14A] hover:shadow-md transition-all duration-300"
          aria-label="Previous client"
          data-testid="carousel-prev-btn"
        >
          <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-[#555555]" />
        </button>

        {/* Slides Container */}
        <div className="overflow-hidden mx-12 md:mx-14">
          <div 
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {clients.map((client) => (
              <div
                key={client.id}
                className="w-full flex-shrink-0 flex justify-center items-center py-2"
              >
                <div 
                  className="bg-white rounded-lg border border-slate-200 w-[140px] h-[100px] sm:w-[160px] sm:h-[115px] md:w-[180px] md:h-[130px] flex items-center justify-center p-4 hover:border-[#C9A14A]/50 hover:shadow-md transition-all duration-300 cursor-pointer"
                  data-testid={`client-logo-${client.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <img 
                    src={client.logo}
                    alt={client.alt}
                    className="max-h-[50px] sm:max-h-[60px] md:max-h-[70px] max-w-[90%] object-contain"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Arrow Right */}
        <button
          onClick={goToNext}
          className="absolute right-0 z-10 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 shadow-sm hover:border-[#C9A14A] hover:shadow-md transition-all duration-300"
          aria-label="Next client"
          data-testid="carousel-next-btn"
        >
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-[#555555]" />
        </button>
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center items-center gap-2 mt-4" data-testid="carousel-dots">
        {clients.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 md:h-2.5 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-[#C9A14A] w-5 md:w-6' 
                : 'bg-slate-300 hover:bg-slate-400 w-2 md:w-2.5'
            }`}
            aria-label={`Go to slide ${index + 1}`}
            data-testid={`carousel-dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ClientCarousel;
